import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTeamStore } from '../../store/teamStore';
import { useAuthStore } from '../../store/authStore';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { subscriptionService } from '../../services/subscriptionService';
import { useFeatureAccess } from '../ProFeatureBadge';
import UpgradeModal from '../UpgradeModal';
import { 
  Target, 
  Plus, 
  ChevronLeft,
  Users,
  Shield,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  X,
  Wand2
} from 'lucide-react';
import type { 
  GamePlan, 
  Player, 
  Team
} from '../../types';
import { format } from 'date-fns';
import { useAIService } from '../../services/aiService.simplified';

// Import modular components
import GamePlanList from './GamePlanList';
import { GamePlanDetail } from './GamePlanDetail';
import GamePlanEditModal from './GamePlanEditModal';
import GamePlanRegenerateModal from './GamePlanRegenerateModal';
import { SubstitutionStrategy } from './SubstitutionStrategy';
import PageHeader from '../layout/PageHeader';

// Game Plan Creation Wizard Steps
enum CreationStep {
  STRATEGIC_QUESTIONS = 'strategic_questions',
  REVIEW_RESULTS = 'review_results'
}

interface GamePlanCreationWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (gamePlanId?: string) => void;
  currentTeam: Team;
  allPlayers: Player[];
  checkStatusColumnExists: () => Promise<boolean>;
  checkLocationColumnExists: () => Promise<boolean>;
  checkDraftDataColumnExists: () => Promise<boolean>;
  forceClear?: boolean;
  gamePlanToEdit?: GamePlan | null;
}

// Strategic Questions Form Component
const StrategicQuestionsForm: React.FC<{
  onNext: (data: any) => void;
  onCancel: () => void;
  initialData?: any;
}> = ({ onNext, onCancel, initialData }) => {
  const [formData, setFormData] = useState(initialData || {
    opponent_name: '',
    game_date: '',
    location: '',
    home_away: 'home',
    key_matchups: '',
    special_considerations: '',
    game_time: '',
    opponent_strengths: '',
    opponent_weaknesses: ''
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const { currentTeam } = useTeamStore();
  const { generateGamePlan } = useAIService();

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      console.log('StrategicQuestionsForm updating with initialData:', initialData);
      setFormData(initialData);
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentTeam) {
      toast.error('No team selected');
      return;
    }
    setIsGenerating(true);

    try {
      // Build a structured prompt for the AI
      const prompt = `
        Generate a detailed game plan strategy for ${currentTeam.name} based on the following information:

        Opponent Analysis:
        • Opponent: ${formData.opponent_name}
        • Location: ${formData.location || 'Not specified'}
        • Game Date: ${formData.game_date}
        • Game Time: ${formData.game_time || 'Not specified'}
        • Key Matchups: ${formData.key_matchups || 'Not specified'}
        • Special Considerations: ${formData.special_considerations || 'Not specified'}
        • Opponent Strengths: ${formData.opponent_strengths || 'Not specified'}
        • Opponent Weaknesses: ${formData.opponent_weaknesses || 'Not specified'}

        Please provide a detailed strategy in JSON format with the following structure:
        {
          "offenseStrategy": {
            "overview": "string",
            "keyPoints": ["string"],
            "playRecommendations": [{
              "playType": "string",
              "formation": "string",
              "personnel": "string",
              "situation": "string",
              "notes": "string"
            }],
            "gameSituation": {
              "redZone": "string",
              "shortYardage": "string",
              "twoMinute": "string"
              }
          },
          "defenseStrategy": {
            "overview": "string",
            "keyPoints": ["string"],
            "alignmentRecommendations": [{
              "formation": "string",
              "front": "string",
              "coverage": "string",
              "situation": "string"
            }],
            "blitzPackages": [{
              "type": "string",
              "situation": "string",
              "effectiveness": "string"
            }],
            "gameSituation": {
              "redZone": "string",
              "shortYardage": "string",
              "twoMinute": "string"
            }
          }
        }

        Guidelines:
        1. Focus on exploiting opponent weaknesses while neutralizing their strengths
        2. Consider the key matchups and special considerations provided
        3. Provide specific, actionable strategies
        4. Include situational strategies for different game scenarios
        5. Ensure all responses are detailed and practical
        6. If a key point mentions multiple play types, provide a separate play recommendation for each type
        7. Ensure each play recommendation is specific and actionable, with clear formation and personnel details
        8. Include notes that explain how each play recommendation exploits opponent weaknesses or counters their strengths
        9. Ensure the strategy is detailed and practical, with clear explanations of how to execute each play
      `;

      console.log('[AI Request] Generating game plan with prompt:', prompt);

      const strategies = await generateGamePlan(prompt);
      console.log('[AI Response] Raw response:', strategies);

      try {
        const parsedStrategies = JSON.parse(strategies);
        console.log('[AI Response] Parsed strategies:', parsedStrategies);

        // Transform the detailed response into the format expected by the form
        const transformedStrategies = {
          offensive_strategy: formatStrategyResponse(parsedStrategies.offenseStrategy),
          defensive_strategy: formatStrategyResponse(parsedStrategies.defenseStrategy)
        };

        onNext({
        ...formData,
          ...transformedStrategies
        });
      } catch (parseError) {
        console.error('[AI Error] Failed to parse AI response:', parseError);
        console.error('[AI Error] Raw response that failed to parse:', strategies);
        throw new Error('Failed to parse AI response as JSON. Please try again.');
      }
    } catch (error) {
      console.error('[AI Error] Error generating game plan:', error);
      toast.error('Failed to generate game plan. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Helper function to format the detailed strategy response
  const formatStrategyResponse = (strategy: any) => {
    if (!strategy) {
      console.error('[AI Error] Strategy object is undefined:', strategy);
      return null;
    }

    try {
      return strategy;
    } catch (error) {
      console.error('[AI Error] Error formatting strategy:', error);
      console.error('[AI Error] Strategy object:', strategy);
      return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
            Opponent Name
            </label>
            <input
              type="text"
              value={formData.opponent_name}
              onChange={(e) => setFormData({ ...formData, opponent_name: e.target.value })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
            Game Date
            </label>
            <input
              type="date"
              value={formData.game_date}
              onChange={(e) => setFormData({ ...formData, game_date: e.target.value })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
            Game Time
            </label>
            <input
            type="time"
            value={formData.game_time}
            onChange={(e) => setFormData({ ...formData, game_time: e.target.value })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Home / Away *
            </label>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="home_away"
                  value="home"
                  checked={formData.home_away === 'home'}
                  onChange={(e) => setFormData({ ...formData, home_away: e.target.value })}
                  className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                />
                <span className="ml-2 text-sm text-gray-700">Home</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="home_away"
                  value="away"
                  checked={formData.home_away === 'away'}
                  onChange={(e) => setFormData({ ...formData, home_away: e.target.value })}
                  className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                />
                <span className="ml-2 text-sm text-gray-700">Away</span>
              </label>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Location (Stadium/Venue)
          </label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            placeholder="Stadium name or specific venue"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Opponent Strengths
          </label>
          <textarea
            value={formData.opponent_strengths}
            onChange={(e) => setFormData({ ...formData, opponent_strengths: e.target.value })}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            rows={3}
            placeholder="What does this opponent do well?"
          required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Opponent Weaknesses
          </label>
          <textarea
            value={formData.opponent_weaknesses}
            onChange={(e) => setFormData({ ...formData, opponent_weaknesses: e.target.value })}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            rows={3}
            placeholder="What areas can we exploit?"
          required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
          Key Matchups <span className="text-gray-500 text-xs">(Optional)</span>
          </label>
          <textarea
            value={formData.key_matchups}
            onChange={(e) => setFormData({ ...formData, key_matchups: e.target.value })}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
          rows={3}
          placeholder="Describe the key matchups we need to focus on (optional)"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
          Special Considerations <span className="text-gray-500 text-xs">(Optional)</span>
          </label>
          <textarea
            value={formData.special_considerations}
            onChange={(e) => setFormData({ ...formData, special_considerations: e.target.value })}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
          rows={3}
          placeholder="Any special considerations for this game (optional)"
          />
        </div>

      <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
          disabled={isGenerating}
            className="btn-primary flex items-center gap-2"
          >
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Generating...
              </>
            ) : (
              <>
                Generate Game Plan
              <Wand2 className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </form>
  );
};

// Game Plan Creation Wizard
const GamePlanCreationWizard: React.FC<GamePlanCreationWizardProps> = ({
  isOpen,
  onClose,
  onSuccess,
  currentTeam,
  allPlayers,
  checkStatusColumnExists,
  checkLocationColumnExists,
  checkDraftDataColumnExists,
  forceClear,
  gamePlanToEdit
}) => {
  const [currentStep, setCurrentStep] = useState<CreationStep>(CreationStep.STRATEGIC_QUESTIONS);
  const [draftGamePlanId, setDraftGamePlanId] = useState<string | null>(null);
  const [depthChart, setDepthChart] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    strategicQuestions?: any;
    unavailablePlayers?: Set<string>;
    substitutionStrategy?: any;
  }>({});
  const [isCreating, setIsCreating] = useState(false);
  const [forceClearForm, setForceClearForm] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const lastTeamIdRef = useRef<string | null>(null);

  // Load existing draft when component mounts
  useEffect(() => {
    if (isOpen) {
      if (forceClear) {
        // Clear form data when forceClear is true
        setFormData({});
        setCurrentStep(CreationStep.STRATEGIC_QUESTIONS);
        setDraftGamePlanId(null);
        setError(null);
      } else if (gamePlanToEdit) {
        // Load data from completed game plan being edited
        loadCompletedGamePlanData();
      } else {
        // Load existing draft
        loadExistingDraft();
      }
    }
  }, [isOpen, forceClear]);

  // Handle gamePlanToEdit changes separately
  useEffect(() => {
    if (isOpen && gamePlanToEdit && !forceClear) {
      console.log('gamePlanToEdit changed, loading data...');
      loadCompletedGamePlanData();
    }
  }, [gamePlanToEdit]);

  const loadCompletedGamePlanData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!gamePlanToEdit) return;

      console.log('Loading completed game plan data:', gamePlanToEdit);

      // Try to load data from draft_data first, then fall back to main columns
      let strategicQuestionsData = {
        opponent_name: (gamePlanToEdit as any).opponent_name || '',
        game_date: gamePlanToEdit.game_date,
        game_time: (gamePlanToEdit as any).game_time || '',
        location: (gamePlanToEdit as any).location || '',
        offensive_strategy: gamePlanToEdit.offensive_strategy,
        defensive_strategy: gamePlanToEdit.defensive_strategy,
        key_matchups: (gamePlanToEdit as any).key_matchups || '',
        special_considerations: (gamePlanToEdit as any).special_considerations || '',
        opponent_strengths: '',
        opponent_weaknesses: ''
      };

      // Check if there's draft_data and try to parse it
      if ((gamePlanToEdit as any).draft_data) {
        try {
          const draftData = typeof (gamePlanToEdit as any).draft_data === 'string' 
            ? JSON.parse((gamePlanToEdit as any).draft_data)
            : (gamePlanToEdit as any).draft_data;

          if (draftData.strategicQuestions) {
            strategicQuestionsData = {
              ...strategicQuestionsData,
              ...draftData.strategicQuestions
            };
          }
        } catch (parseError) {
          console.error('Error parsing draft_data:', parseError);
          // Continue with the fallback data
    }
      }

      console.log('Strategic questions data:', strategicQuestionsData);

      setFormData({
        strategicQuestions: strategicQuestionsData,
        unavailablePlayers: new Set(), // Start with all players available
        substitutionStrategy: {} // Will be loaded from depth chart
      });

      setCurrentStep(CreationStep.STRATEGIC_QUESTIONS);
      setDraftGamePlanId(gamePlanToEdit.id);
    } catch (error) {
      console.error('Error loading completed game plan data:', error);
      setError('Failed to load game plan data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadExistingDraft = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const hasDraftDataColumn = await checkDraftDataColumnExists();
      if (!hasDraftDataColumn) {
        console.warn('Draft data column does not exist in game_plans table');
        return;
      }

        const { data, error } = await supabase
          .from('game_plans')
          .select('*')
          .eq('team_id', currentTeam.id)
          .eq('status', 'draft')
        .maybeSingle();

      if (error) {
        if (error.code === 'PGRST116') {
          // No draft found, start fresh
          setCurrentStep(CreationStep.STRATEGIC_QUESTIONS);
          setDraftGamePlanId(null);
          setFormData({});
        return;
      }
        throw error;
      }

      if (data) {
        setDraftGamePlanId(data.id);
        
        // Parse draft data and restore form state
        if (data.draft_data) {
          try {
            // Handle both string and object draft_data
            const draftData = typeof data.draft_data === 'string' 
              ? JSON.parse(data.draft_data)
              : data.draft_data;

            setFormData({
              strategicQuestions: draftData.strategicQuestions || {},
              unavailablePlayers: new Set(draftData.unavailablePlayers || []),
              substitutionStrategy: draftData.substitutionStrategy || {}
            });

            // Set current step based on draft status
            if (draftData.step === CreationStep.REVIEW_RESULTS) {
              setCurrentStep(CreationStep.REVIEW_RESULTS);
            } else {
              setCurrentStep(CreationStep.STRATEGIC_QUESTIONS);
          }
          } catch (parseError) {
            console.error('Error parsing draft data:', parseError);
            setError('Failed to parse draft data');
            setCurrentStep(CreationStep.STRATEGIC_QUESTIONS);
            setDraftGamePlanId(null);
            setFormData({});
          }
        } else {
          // No draft data found, start fresh
          setCurrentStep(CreationStep.STRATEGIC_QUESTIONS);
          setDraftGamePlanId(null);
          setFormData({});
        }
      }
    } catch (error) {
      console.error('Error loading draft:', error);
      setError('Failed to load draft game plan');
      setCurrentStep(CreationStep.STRATEGIC_QUESTIONS);
      setDraftGamePlanId(null);
      setFormData({});
    } finally {
      setIsLoading(false);
    }
  };

  const saveDraftToDB = async (stepData: any, step: CreationStep) => {
    try {
      const draftData = {
        strategicQuestions: step === CreationStep.STRATEGIC_QUESTIONS ? stepData : formData.strategicQuestions,
        unavailablePlayers: Array.from(formData.unavailablePlayers || []),
        substitutionStrategy: step === CreationStep.REVIEW_RESULTS ? stepData.substitutionStrategy : formData.substitutionStrategy,
        step: step
      };

      // Check if columns exist
      const hasStatusColumn = await checkStatusColumnExists();
      const hasLocationColumn = await checkLocationColumnExists();
      const hasDraftDataColumn = await checkDraftDataColumnExists();
      
      const gamePlanRecord: any = {
        team_id: currentTeam.id,
        updated_at: new Date().toISOString(),
        ...(step === CreationStep.STRATEGIC_QUESTIONS && {
          opponent_name: stepData.opponent_name,
          game_date: stepData.game_date,
          offensive_strategy: stepData.offensive_strategy,
          defensive_strategy: stepData.defensive_strategy,
          key_matchups: stepData.key_matchups || '',
          special_considerations: stepData.special_considerations || '',
          game_time: stepData.game_time || ''
        })
      };

      // Add location if the column exists
      if (hasLocationColumn && step === CreationStep.STRATEGIC_QUESTIONS) {
        gamePlanRecord.location = stepData.location;
      }

      // Add status and draft_data if columns exist
      if (hasStatusColumn) {
        gamePlanRecord.status = 'draft';
      }
      
      if (hasDraftDataColumn) {
        gamePlanRecord.draft_data = JSON.stringify({
          ...draftData,
          step: step
        });
      }

      if (draftGamePlanId) {
        // Update existing draft
        const { error } = await supabase
          .from('game_plans')
          .update(gamePlanRecord)
          .eq('id', draftGamePlanId);

        if (error) throw error;
      } else {
        // Create new draft
        const { data, error } = await supabase
          .from('game_plans')
          .insert({
            ...gamePlanRecord,
            created_at: new Date().toISOString()
          })
          .select()
          .single();

        if (error) throw error;
        setDraftGamePlanId(data.id);
      }

      toast.success('Progress saved automatically');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save progress');
    }
  };

  const handleStrategicQuestionsNext = async (data: any) => {
    try {
      setFormData(prev => ({ ...prev, strategicQuestions: data }));
      await saveDraftToDB(data, CreationStep.STRATEGIC_QUESTIONS);
      
      // Complete the game plan and close the wizard
      await handleSubstitutionComplete();
    } catch (error) {
      console.error('Error saving strategic questions:', error);
      setError('Failed to save strategic questions');
    }
  };

  const handleSubstitutionComplete = async () => {
    try {
      // Save the final game plan
        const hasStatusColumn = await checkStatusColumnExists();
        
      const gamePlanRecord: any = {
        team_id: currentTeam.id,
        updated_at: new Date().toISOString(),
        ...(hasStatusColumn && { status: 'completed' })
      };

      if (draftGamePlanId) {
        const { error } = await supabase
          .from('game_plans')
          .update(gamePlanRecord)
          .eq('id', draftGamePlanId);

        if (error) throw error;
      }

      toast.success('Game plan created successfully!');
      onSuccess(draftGamePlanId || undefined);
    } catch (error) {
      console.error('Error completing game plan:', error);
      setError('Failed to complete game plan');
    }
  };

  const handleBack = () => {
    switch (currentStep) {
      case CreationStep.REVIEW_RESULTS:
        setCurrentStep(CreationStep.STRATEGIC_QUESTIONS);
        break;
      default:
        setCurrentStep(CreationStep.STRATEGIC_QUESTIONS);
    }
  };

  const handleClose = () => {
    setFormData({});
    setCurrentStep(CreationStep.STRATEGIC_QUESTIONS);
    setDraftGamePlanId(null);
    setError(null);
      onClose();
  };

  const handleSuccess = () => {
    onSuccess(draftGamePlanId || undefined);
  };

  if (!isOpen) return null;

    return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[calc(100vh-8rem)] overflow-y-auto mt-16">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Create Game Plan</h2>
                <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
              >
            <X className="h-6 w-6" />
              </button>
          </div>
          
        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded">
            {error}
                </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
          {currentStep === CreationStep.STRATEGIC_QUESTIONS && (
            <StrategicQuestionsForm
              onNext={handleStrategicQuestionsNext}
                onCancel={handleClose}
                initialData={formData.strategicQuestions}
            />
          )}

            {currentStep === CreationStep.REVIEW_RESULTS && (
            <div className="space-y-6">
              <div>
                  <h3 className="text-lg font-semibold mb-2">Review Game Plan</h3>
                  <p className="text-gray-600">Review your completed game plan before finalizing.</p>
              </div>
              
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800">Game plan created successfully!</p>
                </div>

              <div className="flex justify-between pt-4">
                <button
                    onClick={handleBack}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
                <button
                    onClick={() => handleSuccess()}
                  className="btn-primary flex items-center gap-2"
                >
                  Complete Game Plan
                  <CheckCircle className="w-4 h-4" />
                </button>
              </div>
            </div>
            )}
          </>
          )}
      </div>
    </div>
  );
};

interface StrategyDisplayProps {
  strategy: {
    overview: string;
    keyPoints: {
      exploitWeaknesses?: string;
      maximizeStrengths?: string;
      neutralizeStrengths?: string;
      takeAdvantage?: string;
      playRecommendations?: Array<{
        playType: string;
        formation: string;
        personnel: string;
        situation: string;
      }>;
      alignmentRecommendations?: Array<{
        formation: string;
        front: string;
        coverage: string;
        situation: string;
      }>;
      formationSuggestions?: string[];
      blitzPackages?: Array<{
        type: string;
        situation: string;
        effectiveness: string;
      }>;
      gameSituation: string;
    };
  };
  type: 'offense' | 'defense';
}

const StrategyDisplay: React.FC<StrategyDisplayProps> = ({ strategy, type }) => {
  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-lg font-semibold mb-2">Overview</h4>
        <p className="text-gray-700">{strategy.overview}</p>
      </div>

      <div>
        <h4 className="text-lg font-semibold mb-2">Key Points</h4>
        <ul className="list-disc list-inside space-y-2">
          {strategy.keyPoints.exploitWeaknesses && (
            <li className="text-gray-700">{strategy.keyPoints.exploitWeaknesses}</li>
          )}
          {strategy.keyPoints.maximizeStrengths && (
            <li className="text-gray-700">{strategy.keyPoints.maximizeStrengths}</li>
          )}
          {strategy.keyPoints.neutralizeStrengths && (
            <li className="text-gray-700">{strategy.keyPoints.neutralizeStrengths}</li>
          )}
          {strategy.keyPoints.takeAdvantage && (
            <li className="text-gray-700">{strategy.keyPoints.takeAdvantage}</li>
          )}
        </ul>
      </div>

      {strategy.keyPoints.playRecommendations && strategy.keyPoints.playRecommendations.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold mb-2">Play Recommendations</h4>
          <div className="space-y-4">
            {strategy.keyPoints.playRecommendations.map((play, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium">{play.playType}</p>
                <p className="text-sm text-gray-600">Formation: {play.formation}</p>
                <p className="text-sm text-gray-600">Personnel: {play.personnel}</p>
                <p className="text-sm text-gray-600">Situation: {play.situation}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {strategy.keyPoints.alignmentRecommendations && strategy.keyPoints.alignmentRecommendations.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold mb-2">Alignment Recommendations</h4>
          <div className="space-y-4">
            {strategy.keyPoints.alignmentRecommendations.map((alignment, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium">{alignment.formation}</p>
                <p className="text-sm text-gray-600">Front: {alignment.front}</p>
                <p className="text-sm text-gray-600">Coverage: {alignment.coverage}</p>
                <p className="text-sm text-gray-600">Situation: {alignment.situation}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {strategy.keyPoints.formationSuggestions && strategy.keyPoints.formationSuggestions.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold mb-2">Formation Suggestions</h4>
          <ul className="list-disc list-inside space-y-2">
            {strategy.keyPoints.formationSuggestions.map((formation, index) => (
              <li key={index} className="text-gray-700">{formation}</li>
            ))}
          </ul>
        </div>
      )}

      {strategy.keyPoints.blitzPackages && strategy.keyPoints.blitzPackages.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold mb-2">Blitz Packages</h4>
          <div className="space-y-4">
            {strategy.keyPoints.blitzPackages.map((blitz, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium">{blitz.type}</p>
                <p className="text-sm text-gray-600">Situation: {blitz.situation}</p>
                <p className="text-sm text-gray-600">Effectiveness: {blitz.effectiveness}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {strategy.keyPoints.gameSituation && (
      <div>
          <h4 className="text-lg font-semibold mb-2">Game Situation</h4>
        <p className="text-gray-700">{strategy.keyPoints.gameSituation}</p>
      </div>
      )}
    </div>
  );
};

type GamePlanWithAnalysis = GamePlan;

export default function GamePlanManagement() {
  const { currentTeam, fetchAllTeams } = useTeamStore();
  const { user } = useAuthStore();
  const [gamePlans, setGamePlans] = useState<GamePlanWithAnalysis[]>([]);
  const [selectedGamePlan, setSelectedGamePlan] = useState<GamePlanWithAnalysis | null>(null);
  const [gamePlanToEdit, setGamePlanToEdit] = useState<GamePlan | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileView, setIsMobileView] = useState(false);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [forceClearForm, setForceClearForm] = useState(false);
  const lastTeamIdRef = useRef<string | null>(null);

  // Pro feature access
  const { checkFeatureAccess, showUpgradeModal, handleUpgrade, handleCloseModal } = useFeatureAccess();

  // Check if a specific column exists in the game_plans table
  const checkColumnExists = async (columnName: string): Promise<boolean> => {
    try {
      await supabase
        .from('game_plans')
        .select(columnName)
        .limit(1);
      return true;
    } catch (error: any) {
      if (error.code === '42703') {
        console.warn(`Column ${columnName} does not exist in game_plans table. Please run the migration to add this column.`);
        return false;
      }
      throw error;
    }
  };

  // Check if status column exists in the game_plans table
  const checkStatusColumnExists = async (): Promise<boolean> => {
    return checkColumnExists('status');
  };

  // Check if location column exists in the game_plans table
  const checkLocationColumnExists = async (): Promise<boolean> => {
    return checkColumnExists('location');
    };

  // Check if draft_data column exists in the game_plans table
  const checkDraftDataColumnExists = async (): Promise<boolean> => {
    return checkColumnExists('draft_data');
  };

  // Initial load of teams
  useEffect(() => {
    const loadTeams = async () => {
      setIsLoading(true);
      await fetchAllTeams();
      setIsLoading(false);
    };

    loadTeams();
  }, [fetchAllTeams]);

  // Check for mobile view
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  // Fetch game plans when team changes
  useEffect(() => {
    if (currentTeam) {
      fetchGamePlans();
      lastTeamIdRef.current = currentTeam.id;
    }
  }, [currentTeam]);

  // Listen for team change events
  useEffect(() => {
    const handleTeamChange = (event: CustomEvent) => {
      console.log(`[GamePlanManagement] Received team-changed event: ${event.detail.name} (${event.detail.teamId})`);
      
      // Only reload if the team actually changed
      if (event.detail.teamId && event.detail.teamId !== lastTeamIdRef.current) {
        console.log('[GamePlanManagement] Team changed, refreshing game plans');
        lastTeamIdRef.current = event.detail.teamId;
        // Reset game plans and refetch
        setGamePlans([]);
        setSelectedGamePlan(null);
        setIsLoading(true);
        fetchGamePlans();
      }
    };

    // Listen for team changes
    window.addEventListener('team-changed', handleTeamChange as EventListener);
    
    // Cleanup
    return () => {
      window.removeEventListener('team-changed', handleTeamChange as EventListener);
    };
  }, [currentTeam]);

  // Listen for force refresh events
  useEffect(() => {
    const handleForceRefresh = () => {
      console.log('[GamePlanManagement] Received force-team-refresh event, refreshing teams...');
      const loadTeams = async () => {
        setIsLoading(true);
        await fetchAllTeams();
        setIsLoading(false);
      };
      loadTeams();
    };

    window.addEventListener('force-team-refresh', handleForceRefresh);
    return () => window.removeEventListener('force-team-refresh', handleForceRefresh);
  }, [fetchAllTeams]);

  // Fetch all players when team changes
  useEffect(() => {
    if (currentTeam) {
      fetchAllPlayers();
    }
  }, [currentTeam]);

  const fetchGamePlans = useCallback(async () => {
    if (!currentTeam) return;

    try {
      setIsLoading(true);
      
      // Check if status column exists
      const hasStatusColumn = await checkStatusColumnExists();
      
      let gamePlansData;
      if (hasStatusColumn) {
        // Use status column if available
        const { data, error } = await supabase
          .from('game_plans')
          .select('*')
          .eq('team_id', currentTeam.id)
          .in('status', ['completed', 'draft'])
          .order('updated_at', { ascending: false });

        if (error) throw error;
        gamePlansData = data;
      } else {
        // Fallback: get all game plans
        const { data, error } = await supabase
          .from('game_plans')
          .select('*')
          .eq('team_id', currentTeam.id)
          .order('updated_at', { ascending: false });

        if (error) throw error;
        gamePlansData = data;
      }

      const typedGamePlans = (gamePlansData || []) as GamePlanWithAnalysis[];
      setGamePlans(typedGamePlans);
    } catch (error) {
      console.error('Error fetching game plans:', error);
      toast.error('Failed to load game plans');
    } finally {
      setIsLoading(false);
    }
      }, [currentTeam]);

  const fetchAllPlayers = useCallback(async () => {
    if (!currentTeam) return;

    try {
      const { data, error } = await supabase
        .from('players')
        .select('*')
        .eq('team_id', currentTeam.id)
        .order('first_name', { ascending: true });

      if (error) throw error;

      // Map players with proper playParticipation data
      const playersWithParticipation = (data || [])
        .map(player => ({
          ...player,
          playParticipation: player.play_participation as any
        }));

      setAllPlayers(playersWithParticipation);
    } catch (error) {
      console.error('Error fetching players:', error);
      toast.error('Failed to load players');
    }
  }, [currentTeam]);

  const handleGamePlanSelect = (gamePlan: GamePlanWithAnalysis) => {
    setSelectedGamePlan(gamePlan);
  };

  const handleEditGamePlan = async (gamePlan: GamePlan) => {
    try {
      const { data, error } = await supabase
        .from('game_plans')
        .select('*')
        .eq('id', gamePlan.id)
        .single();

      if (error) throw error;
      
      // Set the game plan to edit first
      setGamePlanToEdit(data);
      
      // Then open the edit modal (not the creation wizard)
      setIsEditModalOpen(true);
    } catch (error) {
      console.error('Error loading game plan for editing:', error);
      toast.error('Failed to load game plan for editing');
    }
  };

  const handleGamePlanRegenerate = (gamePlan: GamePlanWithAnalysis) => {
    setGamePlanToEdit(gamePlan);
    setIsRegenerateModalOpen(true);
  };

  const handleGamePlanDelete = async (gamePlanId: string) => {
    try {
      const { error } = await supabase
        .from('game_plans')
        .delete()
        .eq('id', gamePlanId);

      if (error) throw error;

      setGamePlans(prev => prev.filter(gp => gp.id !== gamePlanId));
      toast.success('Game plan deleted successfully');
    } catch (error) {
      console.error('Error deleting game plan:', error);
      toast.error('Failed to delete game plan');
    }
  };

  const handleBackToList = () => {
    setSelectedGamePlan(null);
  };

  const handlePlayerUpdate = (playerId: string, updates: Partial<Player>) => {
    // Update the player in the allPlayers array
    setAllPlayers(prev => prev.map(player => 
      player.id === playerId ? { ...player, ...updates } : player
    ));
  };

  const handleEditModalSuccess = () => {
    setIsEditModalOpen(false);
    setGamePlanToEdit(null);
    fetchGamePlans();
  };

  const handleRegenerateModalSuccess = () => {
    setIsRegenerateModalOpen(false);
    setGamePlanToEdit(null);
    fetchGamePlans();
  };

  const handleCreationSuccess = async (gamePlanId?: string) => {
    setIsCreating(false);
    setForceClearForm(false);
    setGamePlanToEdit(null);
    
    // Increment game plan generation count for rookie users
    if (user?.id) {
      try {
        await subscriptionService.incrementGamePlanGenerationCount(user.id);
      } catch (error) {
        console.error('Error incrementing game plan generation count:', error);
        // Don't show error to user as the main operation succeeded
      }
    }
    
    if (gamePlanId) {
      // Find the newly created game plan and select it to show details
      fetchGamePlans().then(() => {
        const newlyCreatedGamePlan = gamePlans.find(gp => gp.id === gamePlanId);
        if (newlyCreatedGamePlan) {
          // Select the game plan to show its details (same as clicking on a tile)
          setSelectedGamePlan(newlyCreatedGamePlan);
        }
      });
    } else {
      // Fallback: just refresh the list
    fetchGamePlans();
    }
  };

  const handleContinueDraft = (gamePlan: GamePlanWithAnalysis) => {
    // This will be handled by the wizard's loadExistingDraft function
    setIsCreating(true);
  };

  const handleCreateNewGamePlan = () => {
    // Check Pro access for AI game plan generation
    if (!checkFeatureAccess('AI-powered game planning')) {
      return;
    }
    
    setForceClearForm(true);
    setIsCreating(true);
  };

  // No team selected
  if (!currentTeam) {
    return (
      <div className="container mx-auto py-4 px-4">
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <h3 className="text-lg font-semibold mb-2">No Team Selected</h3>
          <p className="text-gray-600">
            Please select a team from the dashboard or settings page before viewing game plans.
          </p>
        </div>
      </div>
    );
  }

  // Only allow football teams to access game plans
  if (currentTeam.sport !== 'Football') {
  return (
      <div className="container mx-auto py-4 px-4">
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <h3 className="text-lg font-semibold mb-2">Game Plans Not Available</h3>
          <p className="text-gray-600">
            Game plans are currently only available for football teams. Your team is set to {currentTeam.sport}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-4">
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          <PageHeader
            title="Game Plans"
            icon={<Target className="h-6 w-6" />}
            actions={
              <button
                onClick={handleCreateNewGamePlan}
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Create New
              </button>
            }
          />
          
          <div className="flex flex-col md:flex-row gap-6">
            {/* Left column - Game Plans List (hidden on mobile when game plan is selected) */}
            <div className={`md:w-1/3 ${isMobileView && selectedGamePlan ? 'hidden' : 'block'}`}>
              <GamePlanList
                gamePlans={gamePlans}
                selectedGamePlanId={selectedGamePlan?.id || null}
                onGamePlanSelect={handleGamePlanSelect}
                onEditGamePlan={handleEditGamePlan}
                onDeleteGamePlan={handleGamePlanDelete}
                onAddGamePlan={handleCreateNewGamePlan}
                isMobileView={isMobileView}
                currentTeam={currentTeam}
                mprPlayerCount={allPlayers.filter(player => player.playParticipation?.hasMPR).length}
                onRefresh={fetchGamePlans}
              />
            </div>
            
            {/* Right column - Game Plan Details (hidden on mobile when no game plan is selected) */}
            <div className={`md:w-2/3 ${isMobileView && !selectedGamePlan ? 'hidden' : 'block'}`}>
              {selectedGamePlan && currentTeam ? (
                <GamePlanDetail
                  gamePlan={{ ...selectedGamePlan, team: currentTeam }}
                  allPlayers={allPlayers}
                  onEdit={() => handleEditGamePlan(selectedGamePlan)}
                  onRegenerate={() => handleGamePlanRegenerate(selectedGamePlan)}
                  onBackToList={isMobileView ? handleBackToList : undefined}
                  onPlayerUpdate={handlePlayerUpdate}
                />
              ) : !isMobileView ? (
                <div className="text-center py-12 bg-white rounded-lg shadow-md">
                  <Target className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Game Plan Selected</h3>
                  <p className="text-gray-500 mb-4">Select a game plan from the list to view its details</p>
                </div>
              ) : null}
            </div>
          </div>
        </>
      )}

      {/* Game Plan Creation Wizard */}
      <GamePlanCreationWizard
        isOpen={isCreating}
        onClose={() => {
          setIsCreating(false);
          setForceClearForm(false);
          setGamePlanToEdit(null);
        }}
        onSuccess={handleCreationSuccess}
        currentTeam={currentTeam}
        allPlayers={allPlayers}
        checkStatusColumnExists={checkStatusColumnExists}
        checkLocationColumnExists={checkLocationColumnExists}
        checkDraftDataColumnExists={checkDraftDataColumnExists}
        forceClear={forceClearForm}
        gamePlanToEdit={gamePlanToEdit}
      />

      {/* Edit Game Plan Modal */}
      {gamePlanToEdit && (
        <GamePlanEditModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setGamePlanToEdit(null);
          }}
          gamePlan={gamePlanToEdit}
          onSuccess={handleEditModalSuccess}
        />
      )}

      {/* Regenerate Game Plan Modal */}
      {gamePlanToEdit && (
        <GamePlanRegenerateModal
          isOpen={isRegenerateModalOpen}
          onClose={() => {
            setIsRegenerateModalOpen(false);
            setGamePlanToEdit(null);
          }}
          onSuccess={handleRegenerateModalSuccess}
          gamePlan={gamePlanToEdit}
        />
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={handleCloseModal}
        onUpgrade={handleUpgrade}
      />
    </div>
  );
} 